// lib/api.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get auth token from localStorage
const getToken = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('adminToken');
  }
  return null;
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    throw new Error(error.message || `Error: ${response.status}`);
  }
  return response.json();
};

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return handleResponse(response);
  },
};

// Tours API
export const toursAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    const queryString = params.toString();
    const url = `${API_BASE_URL}/tours${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url);
    return handleResponse(response);
  },

  getById: async (id) => {
    const response = await fetch(`${API_BASE_URL}/tours/${id}`);
    return handleResponse(response);
  },

  create: async (tourData) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/tours`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(tourData),
    });
    return handleResponse(response);
  },

  update: async (id, tourData) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/tours/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(tourData),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/tours/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error('Failed to delete tour');
    }
    return true;
  },
};

// Bookings API
export const bookingsAPI = {
  create: async (bookingData) => {
    const response = await fetch(`${API_BASE_URL}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData),
    });
    return handleResponse(response);
  },

  getAll: async (params = {}) => {
    const token = getToken();
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_BASE_URL}/bookings?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return handleResponse(response);
  },

  getById: async (id) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/bookings/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    return handleResponse(response);
  },

  updateStatus: async (id, status) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/bookings/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });
    return handleResponse(response);
  },
};

// Blogs API
export const blogsAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_BASE_URL}/blogs?${queryParams}`);
    return handleResponse(response);
  },

  create: async (blogData) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/blogs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(blogData),
    });
    return handleResponse(response);
  },

  update: async (id, blogData) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/blogs/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(blogData),
    });
    return handleResponse(response);
  },

  delete: async (id) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/blogs/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error('Failed to delete blog');
    }
    return true;
  },
};

// Discounts API
export const discountsAPI = {
  getAll: async () => {
    const response = await fetch(`${API_BASE_URL}/discounts`);
    return handleResponse(response);
  },

  apply: async (code) => {
    const response = await fetch(`${API_BASE_URL}/discounts/apply/${code}`);
    return handleResponse(response);
  },

  create: async (discountData) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/discounts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(discountData),
    });
    return handleResponse(response);
  },
};